import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, fromEvent, Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class StickyOffsetService implements OnDestroy {
  private offsets = new Map<string, BehaviorSubject<number>>()
  private destroy$ = new Subject<void>()

  constructor() {
    // Listen for window resize to recalculate offsets
    fromEvent(window, 'resize')
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.recalculateAll())
  }

  /**
   * Get an observable of the sticky offset for elements below the specified reference element.
   * @param referenceElementId The ID of the element to measure from (offset will be its bottom edge)
   * @returns Observable<number> The pixel offset for sticky positioning
   */
  getOffset$(referenceElementId: string): Observable<number> {
    if (!this.offsets.has(referenceElementId)) {
      const initialOffset = this.calculateOffset(referenceElementId)
      this.offsets.set(referenceElementId, new BehaviorSubject<number>(initialOffset))
    }
    return this.offsets.get(referenceElementId).asObservable()
  }

  /**
   * Get the current sticky offset value synchronously.
   * @param referenceElementId The ID of the element to measure from
   * @returns number The pixel offset for sticky positioning
   */
  getOffset(referenceElementId: string): number {
    return this.calculateOffset(referenceElementId)
  }

  /**
   * Calculate the offset as the sticky top + height of the reference element.
   * Checks the element itself and its first child for sticky/fixed positioning,
   * since Angular component host elements may wrap the actual sticky element.
   */
  private calculateOffset(referenceElementId: string): number {
    const element = document.getElementById(referenceElementId)
    if (!element) {
      console.warn(`StickyOffsetService: Element with ID "${referenceElementId}" not found`)
      return 0
    }

    const stickyEl = this.findStickyElement(element)
    const styles = getComputedStyle(stickyEl)
    const top = parseFloat(styles.top) || 0
    const height = stickyEl.getBoundingClientRect().height
    return top + height
  }

  /**
   * Find the effective sticky/fixed element — either the element itself
   * or its first child (for Angular component host wrappers).
   */
  private findStickyElement(element: HTMLElement): HTMLElement {
    const position = getComputedStyle(element).position
    if (position === 'sticky' || position === 'fixed') {
      return element
    }
    const firstChild = element.firstElementChild as HTMLElement
    if (firstChild) {
      const childPosition = getComputedStyle(firstChild).position
      if (childPosition === 'sticky' || childPosition === 'fixed') {
        return firstChild
      }
    }
    return element
  }

  /**
   * Manually trigger a recalculation for a specific reference element.
   */
  recalculate(referenceElementId: string): void {
    const subject = this.offsets.get(referenceElementId)
    if (subject) {
      const newOffset = this.calculateOffset(referenceElementId)
      subject.next(newOffset)
    }
  }

  /**
   * Recalculate all tracked offsets.
   */
  recalculateAll(): void {
    this.offsets.forEach((subject, id) => {
      const newOffset = this.calculateOffset(id)
      subject.next(newOffset)
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
